/* eslint-disable @typescript-eslint/no-explicit-any */
// mediumSignupSanitizer.ts
import type { MediumSignupPayload } from "../types";

/**
 * Custom sanitizer for medium signup that respects backend validation requirements
 */
export function sanitizeMediumSignupPayload(
  payload: Partial<MediumSignupPayload>,
): MediumSignupPayload {
  const sanitized: any = { ...payload };

  const normalizeBirthDate = (birthDate: string): string => {
    if (!birthDate) return "";

    const trimmed = birthDate.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed;
    }

    if (/^\d{4}-\d{2}-\d{2}T/.test(trimmed)) {
      return trimmed.slice(0, 10);
    }

    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) {
      return trimmed;
    }

    return parsed.toISOString().slice(0, 10);
  };

  // Preserve required fields even if empty (they'll be validated by the backend)
  const requiredFields = [
    "firstName",
    "lastName",
    "email",
    "birthDate",
    "initiationDate",
    "documentType",
    "document",
    "phone",
    "contact",
    "rankId",
    "roleId",
    "street",
    "number",
    "neighborhood",
    "city",
    "state",
    "zipCode",
    "password",
    "confirmPassword",
  ];

  // Ensure required fields are at least empty strings if missing
  for (const field of requiredFields) {
    if (sanitized[field] === undefined || sanitized[field] === null) {
      sanitized[field] = "";
    }
  }

  sanitized.birthDate = normalizeBirthDate(String(sanitized.birthDate ?? ""));

  // For guardian fields, only sanitize if user is not a minor
  // You'll need to calculate age based on birthDate
  const age = calculateAgeFromBirthDate(sanitized.birthDate);
  if (age >= 18) {
    // Remove guardian fields for adults
    delete sanitized.guardianName;
    delete sanitized.guardianPhone;
    delete sanitized.guardianRelationship;
  } else {
    // Ensure guardian fields exist for minors (validation will catch if they're empty)
    if (
      sanitized.guardianName === undefined ||
      sanitized.guardianName === null
    ) {
      sanitized.guardianName = "";
    }
    if (
      sanitized.guardianPhone === undefined ||
      sanitized.guardianPhone === null
    ) {
      sanitized.guardianPhone = "";
    }
    if (
      sanitized.guardianRelationship === undefined ||
      sanitized.guardianRelationship === null
    ) {
      sanitized.guardianRelationship = "";
    }
  }

  // Remove any other fields not in the schema
  const allowedFields = [
    ...requiredFields,
    "complement", // Optional field
    "code",
    "signupCode",
    "guardianName",
    "guardianPhone",
    "guardianRelationship", // Conditional fields
  ];

  Object.keys(sanitized).forEach((key) => {
    if (!allowedFields.includes(key)) {
      delete sanitized[key];
    }
  });

  return sanitized as MediumSignupPayload;
}

// Helper function to calculate age
function calculateAgeFromBirthDate(birthDate: string): number {
  // Implement age calculation logic
  const birth = new Date(birthDate);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }

  return age;
}
